import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { AddEmailTemplateRequest, ProductIds, addEmailTemplate } from 'core'

import { Button, Card, Col, DefaultRadioGroupV2, HtmlEditor, InputV2, Notification, Row } from 'common/components'
import { useCommandRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

type CompleteEmailTemplateFormValue = {
  id?: string
  subject?: string
  productId?: number | null
  content?: string
}

const AddEmailTemplate = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const [initialValues, setInitialValues] = useState<CompleteEmailTemplateFormValue>({
    id: '',
    subject: '',
    content: '',
    productId: null,
  })
  const { sendCommand, isLoading } = useCommandRequestV2((arg: AddEmailTemplateRequest) => addEmailTemplate(arg))

  const submitData = async (values: CompleteEmailTemplateFormValue) => {
    const dataForSubmit: AddEmailTemplateRequest = {
      id: values.id,
      subject: values.subject,
      productId: values.productId,
      wrapperId: 'default-wrapper-v3',
      content: values.content,
    }

    const result = await sendCommand(dataForSubmit)

    if (result.completed) {
      Notification.success(translate('csi-dashboard.add-email-template.add-success-message'))
      history.push(ROUTE_PATHS.EMAIL_TEMPLATES)
    }
  }

  return (
    <Row>
      <Col span={16}>
        <Card className="add-email-template-wrapper" title={'Add template'}>
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator(translate)}
            initialValues={initialValues}
            onSubmit={submitData}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <InputV2 name="id" label="Id" placeholder="Template Id" />

                <InputV2 name="subject" label="Subject" placeholder="Subject of email" />

                <DefaultRadioGroupV2
                  label="Select product"
                  name={'productId'}
                  type="button"
                  direction="horizontal"
                  items={[
                    { name: 'All Products', value: null },
                    { name: 'ICHRA', value: ProductIds.IchraId },
                    { name: 'QSEHRA', value: ProductIds.QsehraId },
                    { name: 'GCHRA', value: ProductIds.GroupHraId },
                    { name: 'Perks', value: ProductIds.Perks },
                  ]}
                />

                <HtmlEditor name="content" value={initialValues.content} label="Content" />

                <Button
                  htmlType="submit"
                  disabled={!formValidator(translate).isValidSync(formik.values) || isLoading}
                  type="primary"
                  loading={isLoading}
                >
                  {translate('general.save')}
                </Button>

                <Button
                  onClick={() => {
                    history.push(ROUTE_PATHS.EMAIL_TEMPLATES)
                  }}
                  className="ml-16"
                  type="default"
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {translate('general.cancel')}
                </Button>
              </form>
            )}
          </Formik>
        </Card>
      </Col>
    </Row>
  )
}

export default AddEmailTemplate

const formValidator = (translate: any) =>
  Yup.object({
    id: Yup.string().trim().required(translate('general.required')),
    subject: Yup.string().trim().required(translate('general.required')),
    content: Yup.string().trim().required(translate('general.required')),
  })
